/**
 * Seção Rastreamento.
 *
 * Controle, Planejamento e Mapa são itens do menu (ver AppShell). Os filtros
 * moram aqui, no layout comum, porque valem para as três — trocar de
 * "Controle" para "Mapa" mantém a cooperativa escolhida, e a resposta vem do
 * cache.
 */

import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { numero } from '../../lib/format.ts';
import { Aviso } from '../../components/ui/index.tsx';
import BarraFiltros, {
  filtroInicial,
  type OpcoesFiltro,
  type ValorFiltro,
} from '../../components/filtros/BarraFiltros.tsx';
import type { ContextoRastreamento, Resposta } from './dados.ts';

export default function RastreamentoLayout() {
  const [filtro, setFiltro] = useState<ValorFiltro>(filtroInicial);

  const params = new URLSearchParams();
  if (filtro.cooperativa) params.set('cooperativa', filtro.cooperativa);
  if (filtro.consultor) params.set('consultor', filtro.consultor);
  const qs = params.toString();

  const { data: opcoes } = useQuery({
    queryKey: ['vendas', 'filtros'],
    queryFn: () => api.get<OpcoesFiltro>('/vendas/filtros'),
    staleTime: 10 * 60_000,
  });

  // Mesma chave das abas: só lê o cache para o aviso de cobertura.
  const { data } = useQuery({
    queryKey: ['rastreamento', qs],
    queryFn: () => api.get<Resposta>(`/rastreamento?${qs}`),
  });
  const cob = data?.cobertura;

  return (
    <div className="flex flex-col gap-5">
      <div className="imprimir-oculto flex flex-col gap-3">
        <BarraFiltros valor={filtro} onChange={setFiltro} opcoes={opcoes} semPeriodo />

        {/* Nada lido ainda é um estado próprio, não "0 pendências": sem este
            aviso, os zeros abaixo pareciam a carteira em dia. */}
        {cob && cob.total > 0 && cob.consultadas === 0 ? (
          <Aviso tom="alerta" icone={<Loader2 size={14} className="animate-spin" />}>
            A leitura dos produtos no SGA ainda não começou — por isso os números abaixo estão
            zerados. Ela é feita aos poucos, cerca de 150 placas por hora, e os casos aparecem
            conforme avança.
          </Aviso>
        ) : (
          cob &&
          cob.consultadas < cob.total && (
            <Aviso>
              Produtos consultados em {numero(cob.consultadas)} de {numero(cob.total)} placas
              {cob.ultimaConsulta
                ? ` (última leitura ${new Date(cob.ultimaConsulta).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })})`
                : ''}
              . A leitura no SGA é gradual; as que faltam ficam em “Aguardando”.
            </Aviso>
          )
        )}
      </div>

      <Outlet context={{ qs } satisfies ContextoRastreamento} />
    </div>
  );
}
